import { getPublicData } from "../../../../../../services/publicPegasApi/MakePublicOrder";
/**
 * PrintDailyReport
 * - لو printData Array => يطبع تقرير يوميات (sessions) على حراري
 * - لو printData Object => يطبع فاتورة/أوردر (المنطق القديم) على حراري
 *
 * توصيات العرض:
 * - XP-80C (رول 80): options.paperWidthMm = 72.1 و options.printableWidthMm = 72.1
 * - ورق 80 حقيقي: options.paperWidthMm = 80
 * - ورق 88 حقيقي: options.paperWidthMm = 88 (فقط إذا الدرايفر يدعم 88 فعليًا)
 */
export default async function PrintDailyReportsShortcut(
  printData = {},
  invoiceData = {},
  orderId,
  options = {}
) {
  let publicData = {};
  try {
    publicData = await getPublicData();
    console.log("📞 بيانات الشركة المسترجعة:", publicData);
  } catch (err) {
    console.error("❌ فشل في جلب البيانات العامة:", err);
  }

  const logoUrl = publicData?.logo ? publicData.logo.trim().replace(/\s+/g, "") : "";
  console.log("🖼️ رابط اللوجو النهائي:", logoUrl);

  // =========================
  // ✅ THERMAL CONFIG
  // =========================
  const paperWidthMm = Number(options?.paperWidthMm ?? 72.1);
  const printableWidthMm = Number(options?.printableWidthMm ?? paperWidthMm);
  const safePrintableWidthMm = Math.max(48, Math.min(printableWidthMm, paperWidthMm));
  const fontSize = options?.fontSize ? Number(options.fontSize) : 16;
  const headingBoost = options?.headingBoost ? Number(options.headingBoost) : 3;
  const lineHeight = options?.lineHeight ? Number(options.lineHeight) : 1.3;

  const formatMoney = (v) => {
    const n = parseFloat(v);
    return (isNaN(n) ? 0 : n).toFixed(2);
  };

  const normalizeType = (t) => String(t || "").trim().toLowerCase();

  // =========================
  // ✅ MULTI-SESSION DAILY REPORT - SHORTCUT (SUMMARY MODE)
  // =========================
  if (Array.isArray(printData)) {
    const sessions = printData;
    const title = options?.title || "تقرير الجلسات المختصر";

    const rowsHtml = sessions
      .map((s) => {
        const grouped = s.grouped_transactions_by_shift || {};

        // ✅ اجماليات اليومية (Invoices / Deposit / Withdrawal / Expenses)
        let sessionInvoicesTotal = 0;
        let sessionDepositTotal = 0;
        let sessionWithdrawalTotal = 0;
        let sessionExpensesTotal = 0;

        Object.values(grouped).forEach((txArray) => {
          (txArray || []).forEach((tx) => {
            const type = normalizeType(tx?.type);
            const amt = parseFloat(tx?.amount || 0) || 0;

            // الفاتورة: الأفضل الاعتماد على invoice.total_amount لو موجود
            if (tx?.invoice?.total_amount != null) {
              sessionInvoicesTotal += parseFloat(tx.invoice.total_amount) || 0;
              return;
            }

            // fallback لو tx.type === invoice
            if (type === "invoice") {
              sessionInvoicesTotal += amt;
              return;
            }

            if (type === "deposit") sessionDepositTotal += amt;
            else if (type === "withdrawal") sessionWithdrawalTotal += amt;
            else if (type === "expenses" || type === "expense") sessionExpensesTotal += amt;
          });
        });

        const netSession = sessionInvoicesTotal + sessionDepositTotal - sessionWithdrawalTotal - sessionExpensesTotal;

        // 🎯 بناء صفوف أنواع الفواتير (invoiceTypes)
        const invoiceTypesRows = (() => {
          const invoiceTypes = s.invoiceTypes || {};
          if (Object.keys(invoiceTypes).length === 0) return '';
          
          return Object.entries(invoiceTypes)
            .map(([type, data]) => {
              const typeAr = type === 'delivery' ? 'ديليفوي' : 
                           type === 'tables' ? 'طاولات' : 
                           type === 'takeaway' ? 'تيك اواي' : type;
              return `
                <tr class="invoice-type-row">
                  <td class="label-col indent">&nbsp;&nbsp;${typeAr}:</td>
                  <td class="val-col">${data.count} فاتورة - ${formatMoney(data.total)} ج</td>
                </tr>
              `;
            })
            .join('');
        })();

        // 🎯 بناء صفوف تفاصيل الفاتورة (الضريبة والخصم والتوصيل والخدمات) + عدد المرات
        const invoiceDetailsRows = (() => {
          const details = s.invoiceDetails || {};
          const service = parseFloat(details.service || 0) || 0;
          const deliveryService = parseFloat(details.deliveryService || 0) || 0;
          const tax = parseFloat(details.tax || 0) || 0;
          const discount = parseFloat(details.discount || 0) || 0;
          
          const serviceCount = details.serviceCount || 0;
          const deliveryServiceCount = details.deliveryServiceCount || 0;
          const taxCount = details.taxCount || 0;
          const discountCount = details.discountCount || 0;

          let rows = '';
          if (service > 0) {
            rows += `<tr class="details-row"><td class="label-col indent">&nbsp;&nbsp;الخدمات:</td><td class="val-col">${formatMoney(service)} ج (${serviceCount})</td></tr>`;
          }
          if (deliveryService > 0) {
            rows += `<tr class="details-row"><td class="label-col indent">&nbsp;&nbsp;التوصيل:</td><td class="val-col">${formatMoney(deliveryService)} ج (${deliveryServiceCount})</td></tr>`;
          }
          if (tax > 0) {
            rows += `<tr class="details-row"><td class="label-col indent">&nbsp;&nbsp;الضريبة:</td><td class="val-col">${formatMoney(tax)} ج (${taxCount})</td></tr>`;
          }
          if (discount > 0) {
            rows += `<tr class="details-row"><td class="label-col indent">&nbsp;&nbsp;الخصم:</td><td class="val-col">${formatMoney(discount)} ج (${discountCount})</td></tr>`;
          }
          return rows;
        })();

        // 🎯 بناء صفوف الاصناف المحذوفة
        const deletedItemsRows = (() => {
          const deletedSummary = s.deletedItemsSummary || {};
          const totalCount = deletedSummary.totalCount || 0;
          const totalValue = deletedSummary.totalValue || 0;

          if (totalCount === 0) {
            return '';
          }

          let rows = `<tr class="deleted-items-header"><td class="label-col indent" colspan="2"><strong>الاصناف المحذوفة:</strong></td></tr>`;
          
          const items = deletedSummary.items || [];
          items.forEach(item => {
            rows += `<tr class="details-row"><td class="label-col indent">&nbsp;&nbsp;${item.name}:</td><td class="val-col">${item.count} عدد - ${formatMoney(item.totalPrice)} ج</td></tr>`;
          });
          
          rows += `<tr class="deleted-items-total"><td class="label-col indent"><strong>&nbsp;&nbsp;إجمالي المحذوف:</strong></td><td class="val-col"><strong>${totalCount} عدد - ${formatMoney(totalValue)} ج</strong></td></tr>`;
          
          return rows;
        })();

        // 🎯 تصميم مختصر - جدول بالإجماليات فقط
        return `
          <div class="block">
            <h3>اليومية: ${s.session_id ?? "-"}</h3>
            <p class="date-user">التاريخ: ${s.session_date ?? "-"} | ${s.user?.name || "-"}</p>
            
            <table class="summary-table">
              <tr>
                <td class="label-col">الفواتير:</td>
                <td class="val-col">${formatMoney(sessionInvoicesTotal)} ج</td>
              </tr>
              ${invoiceTypesRows}
              ${invoiceDetailsRows}
              ${deletedItemsRows}
              <tr>
                <td class="label-col">الإيداعات:</td>
                <td class="val-col">${formatMoney(sessionDepositTotal)} ج</td>
              </tr>
              <tr>
                <td class="label-col">السحوبات:</td>
                <td class="val-col">${formatMoney(sessionWithdrawalTotal)} ج</td>
              </tr>
              <tr>
                <td class="label-col">المصروفات:</td>
                <td class="val-col">${formatMoney(sessionExpensesTotal)} ج</td>
              </tr>
              <tr class="net-row">
                <td class="label-col"><strong>الإجمالي:</strong></td>
                <td class="val-col"><strong>${formatMoney(netSession)} ج</strong></td>
              </tr>
            </table>
            <hr />
          </div>
        `;
      })
      .join("");

    const html = `<!doctype html>
<html dir="rtl">
<head>
  <meta charset="utf-8" />
  <title>${title}</title>
  <style>
    @page {
      size: ${paperWidthMm}mm auto;
      margin: 0;
    }

    @media print {
      html, body {
        width: 100% !important;
        max-width: 100% !important;
        margin: 0 !important;
        padding: 0 !important;
        background: #fff !important;
        overflow-x: hidden !important;
      }
      * {
        box-sizing: border-box;
        -webkit-print-color-adjust: exact;
        print-color-adjust: exact;
      }
    }

    html, body {
      width: 100%;
      max-width: 100%;
      margin: 0;
      padding: 0;
      background: #fff;
      overflow-x: hidden;
    }

    body {
      font-family: Arial, Helvetica, sans-serif;
      direction: rtl;
      text-align: right;
      line-height: ${lineHeight};
      overflow-wrap: anywhere;
      word-break: break-word;
    }

    .receipt {
      width: 100%;
      max-width: ${safePrintableWidthMm}mm;
      padding: 2.5mm 2mm;
      margin: 0;
      box-sizing: border-box;
      font-size: ${fontSize}px;
      color: #000;
      direction: rtl;
      text-align: right;
      overflow-x: hidden;
      overflow-wrap: anywhere;
      word-break: break-word;
    }

    .logo-wrap { text-align: center; margin: 6px 0 8px; }
    .logo { max-width: 100%; max-height: 60px; object-fit: contain; }

    h1, h2, h3 { margin: 4px 0; text-align: center; }
    h1 { font-size: ${fontSize + headingBoost + 2}px; font-weight: 800; }
    h2 { font-size: ${fontSize + headingBoost}px; font-weight: 800; }
    h3 { font-size: ${fontSize + headingBoost - 1}px; font-weight: 700; }
    p { margin: 3px 0; font-size: ${Math.max(13, fontSize - 1)}px; }
    .date-user { text-align: center; }
    hr { border: none; border-top: 1px solid #000; margin: 6px 0; }

    .block { padding: 6px 0; }

    .summary-table {
      width: 100%;
      border-collapse: collapse;
      table-layout: fixed;
      margin: 4px 0;
      font-size: ${fontSize}px;
    }

    .summary-table tr {
      border-bottom: 1px solid #ddd;
    }

    .label-col {
      width: 50%;
      text-align: right;
      padding: 5px 3px;
      white-space: normal;
      overflow-wrap: anywhere;
      word-break: break-word;
    }

    .val-col {
      width: 50%;
      text-align: left;
      padding: 5px 3px;
      white-space: normal;
      overflow-wrap: anywhere;
      word-break: break-word;
    }

    .net-row {
      border-top: 1px solid #000;
      border-bottom: 1px solid #000;
      font-weight: bold;
    }

    .net-row td {
      padding: 6px 3px;
      font-size: ${fontSize + 2}px;
      font-weight: 800;
    }

    .invoice-type-row {
      background-color: transparent;
      border-bottom: 1px dashed #ddd;
    }

    .invoice-type-row .label-col {
      font-size: ${fontSize - 1}px;
      color: #000;
    }

    .invoice-type-row .val-col {
      font-size: ${fontSize - 1}px;
      color: #000;
    }

    .details-row {
      background-color: transparent;
      border-bottom: 1px dotted #ddd;
    }

    .details-row .label-col {
      font-size: ${fontSize - 1}px;
      color: #000;
    }

    .details-row .val-col {
      font-size: ${fontSize - 1}px;
      color: #000;
    }

    .deleted-items-header {
      background-color: transparent;
      border-bottom: 1px solid #ffc107;
      font-weight: bold;
      padding: 4px 2px;
    }

    .deleted-items-header td {
      font-size: ${fontSize}px;
      color: #000;
      padding: 4px 2px;
    }

    .deleted-items-total {
      background-color: transparent;
      border-top: 1px solid #ffc107;
      border-bottom: 1px solid #ffc107;
      font-weight: bold;
    }

    .deleted-items-total td {
      font-size: ${fontSize}px;
      color: #000;
      padding: 4px 2px;
    }

    .label-col.indent {
      padding-left: 8px;
    }

    .empty { text-align: center; padding: 8px 0; }
  </style>
</head>
<body>
  <div class="receipt">
    ${
      logoUrl
        ? `<div class="logo-wrap">
             <img src="${logoUrl}" class="logo" alt="logo" onerror="this.style.display='none'"/>
           </div>`
        : ""
    }
    <h1>${publicData?.name || "تقرير الجلسات"}</h1>
    <h2>تقرير مختصر</h2>
    <h3>عدد الجلسات: ${sessions.length}</h3>
    <hr />
    ${rowsHtml}
    <p style="text-align:center;margin-top:10px">تم بواسطة نظام ريستو كاش</p>
  </div>
</body>
</html>`;

    return html;
  }

  // =========================
  // ✅ SINGLE ORDER RECEIPT (OLD BEHAVIOR)
  // =========================
  const orderDetails = printData || {};
  const inv = invoiceData || {};
  const items = orderDetails.items || [];

  const subtotal = items.reduce((acc, item) => {
    const price = parseFloat(
      item.price ||
        item.unit_price ||
        (item.total && item.quantity ? item.total / item.quantity : 0) ||
        0
    );
    return acc + (isNaN(price) ? 0 : price) * parseFloat(item.quantity || 0);
  }, 0);

  const service = parseFloat(inv.service || 0);
  const tax = parseFloat(inv.tax || 0);
  const discount = parseFloat(inv.discount || 0);
  const total = subtotal + service + tax - discount;

  const footerText = (() => {
    const base = (inv.footer_text || "").trim();
    const note = (publicData?.invoice_note_printer || "").trim();
    const pieces = [];
    if (base) pieces.push(base);
    if (note) pieces.push(note);
    return pieces.join("<br/>");
  })();

  const address = publicData?.address || "";
  const phone = publicData?.phone || "";

  const orderPrintNumber = inv?.order_print_number || 1;

  return `
<!DOCTYPE html>
<html dir="rtl">
<head>
  <meta charset="UTF-8" />
  <style>
    @page {
      size: ${paperWidthMm}mm auto;
      margin: 0;
    }

    @media print {
      html, body {
        width: 100% !important;
        max-width: 100% !important;
        height: auto !important;
        margin: 0 !important;
        padding: 0 !important;
        background: #fff !important;
        overflow-x: hidden !important;
      }
      * {
        box-sizing: border-box;
        -webkit-print-color-adjust: exact;
        print-color-adjust: exact;
      }
    }

    html, body {
      width: 100%;
      max-width: 100%;
      height: auto;
      margin: 0;
      padding: 0;
      background: #fff;
      overflow-x: hidden;
    }

    .receipt {
      font-family: 'Cairo', Arial, sans-serif;
      width: 100%;
      max-width: ${safePrintableWidthMm}mm;
      padding: 2.8mm 2mm;
      margin: 0;
      font-size: ${fontSize}px;
      color: #000;
      direction: rtl;
      text-align: right;
      line-height: ${lineHeight};
      box-sizing: border-box;
      overflow-x: hidden;
      overflow-wrap: anywhere;
      word-break: break-word;
    }
    h2, h3 { margin: 5px 0; text-align: center; font-weight: 800; }
    h2 { font-size: ${fontSize + headingBoost + 1}px; }
    h3 { font-size: ${fontSize + headingBoost - 1}px; }
    hr { border: none; border-top: 1px dashed #000; margin: 6px 0; }
    table { width: 100%; border-collapse: collapse; table-layout: fixed; }
    th, td {
      padding: 5px 2px;
      text-align: right;
      white-space: normal;
      overflow-wrap: anywhere;
      word-break: break-word;
      vertical-align: top;
    }
    th { border-bottom: 1px dashed #000; }
    .footer { text-align: center; margin-top: 10px; font-size: ${Math.max(13, fontSize - 1)}px; line-height: ${lineHeight}; }
    .logo { max-width: 100%; max-height: 120px; object-fit: contain; display: block; margin: 0 auto 10px; }
  </style>
</head>
<body>
  <div class="receipt">
    ${logoUrl ? `<img src="${logoUrl}" class="logo" alt="Logo" onerror="this.style.display='none'" />` : ""}
    <h2>${inv.header_text || publicData?.name || "تطبيق رستو كاش"}</h2>
    <h3>فاتورة رقم #${orderDetails.general_increment || orderId || "-"}</h3>
    <h3>رقم الفاتورة: ${orderId ?? "-"}</h3>
    <p><strong>عدد مرات الطباعة:</strong> ${orderPrintNumber}</p>
    <hr />
    <p>العميل: ${orderDetails.customer?.name || "بدون اسم"}</p>
    <p>الطاولة: ${orderDetails.table?.name || "غير محددة"}</p>
    <hr />
    <table>
      <thead>
        <tr>
          <th>الصنف</th>
          <th>السعر</th>
          <th>الكمية</th>
          <th>الإجمالي</th>
        </tr>
      </thead>
      <tbody>
        ${items
          .map((item) => {
            const price = parseFloat(
              item.price ||
                item.unit_price ||
                (item.total && item.quantity ? item.total / item.quantity : 0)
            );
            return `
              <tr>
                <td>${item.product_name || "-"}</td>
                <td>${(isNaN(price) ? 0 : price).toFixed(2)} ج</td>
                <td>${item.quantity || 0}</td>
                <td>${(parseFloat(item.total) || 0).toFixed(2)} ج</td>
              </tr>
            `;
          })
          .join("")}
      </tbody>
    </table>
    <hr />
    <p>الإجمالي الفرعي: ${subtotal.toFixed(2)} ج</p>
    <p>الخدمة: ${service.toFixed(2)} ج</p>
    <p>الضريبة: ${tax.toFixed(2)} ج</p>
    <p>الخصم: ${discount.toFixed(2)} ج</p>
    <hr />
    <h3>الإجمالي الكلي: ${total.toFixed(2)} ج</h3>
    <hr />
    <div class="footer">
      ${footerText ? `<p>${footerText}</p>` : `<p>شكرًا لزيارتكم ❤️</p>`}
      ${address ? `<p>📍 العنوان: ${address}</p>` : ""}
      ${phone ? `<p>📞 الهاتف: ${phone}</p>` : ""}
      <p>تم بواسطة نظام ريستو كاش</p>
    </div>
  </div>
</body>
</html>
  `;
}
